import { Component, OnDestroy, computed, inject, input, signal } from '@angular/core';
import { SubSink } from 'subsink';
import { ProjectDataService } from '../../state-and-data-handling/project-data.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { TimeBlockWithTask } from '../../../../../shared/data-types/shared-time-block-types';
import { TimeFormatType } from '../../../../../core/pipes/time-format.pipe';
import { BudgetOptions } from '../../../../../core/constants-repository/project-calculation.constants';
import { faSigma } from '@fortawesome/pro-solid-svg-icons';
import { DoughnutData } from '../../../../../shared/data-types/chart-types';
import { ProjectSummaryAsideService } from './project-summary-aside.service';

@Component({
  selector: 'app-project-summary-aside',
  templateUrl: './project-summary-aside.component.html',
  styleUrl: './project-summary-aside.component.scss',
  standalone: false,
})
export class ProjectSummaryAsideComponent implements OnDestroy {
  // Inputs and signals
  timeBlocksWithTask = input.required<readonly TimeBlockWithTask[]>();
  fuelBarVisible = input.required<boolean>();
  budgetType = signal<BudgetOptions>(null);

  // Billable section
  totalProjectTime = computed(
    () =>
      this.projectSummaryAsideService.billableTime() +
      this.projectSummaryAsideService.nonBillableTime(),
  );
  billableDoughnutData = computed<DoughnutData[]>(() =>
    this.projectSummaryAsideService.buildBillableDoughnutData(
      this.projectSummaryAsideService.billableTime(),
      this.projectSummaryAsideService.nonBillableTime(),
      this.totalProjectTime(),
    ),
  );

  billableDoughnutDataLabels = computed<DoughnutData[]>(() =>
    this.projectSummaryAsideService.buildBillableDoughnutLabels(
      this.projectSummaryAsideService.billableTime(),
      this.projectSummaryAsideService.nonBillableTime(),
      this.totalProjectTime(),
    ),
  );

  // Total project costs section
  expenses = computed(() => this.projectSummaryAsideService.expenses());
  personnelCosts = computed(() => this.projectSummaryAsideService.personnelCosts());

  costDoughnutData = computed<DoughnutData[]>(() =>
    this.projectSummaryAsideService.buildCostDoughnutData(this.expenses(), this.personnelCosts()),
  );

  costDoughnutDataLabels = computed<DoughnutData[]>(() =>
    this.projectSummaryAsideService.buildCostDoughnutLabels(this.expenses(), this.personnelCosts()),
  );

  // Remaining budget section
  unit = computed(() => this.projectSummaryAsideService.unit());
  totalBudget = computed(() => this.projectSummaryAsideService.totalBudget());
  expendituresAbsolut = computed(() => this.projectSummaryAsideService.expendituresAbsolut());
  expendituresInPercent = computed(() =>
    this.projectSummaryAsideService.totalBudget() > 0
      ? (this.expendituresAbsolut() / this.projectSummaryAsideService.totalBudget()) * 100
      : 0,
  );

  // Invoice section
  notInvoiced = signal(-1);
  alreadyInvoiced = signal(-1);

  // Constants
  public readonly TimeFormatType = TimeFormatType;
  public readonly faSigma = faSigma;
  public readonly BudgetOptions = BudgetOptions;
  private readonly subs = new SubSink();

  // DI
  private readonly projectDataService = inject(ProjectDataService);
  private readonly projectSummaryAsideService = inject(ProjectSummaryAsideService);

  constructor() {
    toObservable(this.timeBlocksWithTask).subscribe((timeBlocks) => {
      if (!timeBlocks) {
        return;
      }

      const projectClone = this.projectDataService.getProjectClone();
      this.budgetType.set(projectClone.budgetType);

      // billable and non billable time
      this.projectSummaryAsideService.calcBillableTime([...this.timeBlocksWithTask()]);

      // Costs (expenses + personnel costs)
      this.projectSummaryAsideService.calcTotalProjectCosts([...this.timeBlocksWithTask()]);

      // the amount of remaining budget
      this.projectSummaryAsideService.calcRemainingBudget(projectClone, [
        ...this.timeBlocksWithTask(),
      ]);

      // the amount of fees which was already invoiced
      const alreadyInvoiced = this.projectSummaryAsideService.calcTotalInvoiceAmount(
        [...this.timeBlocksWithTask()],
        projectClone,
        true,
      );
      this.alreadyInvoiced.set(alreadyInvoiced);

      // the amount of fees which has not been invoiced so far
      const notInvoiced = this.projectSummaryAsideService.calcTotalInvoiceAmount(
        [...this.timeBlocksWithTask()],
        projectClone,
        false,
      );
      this.notInvoiced.set(notInvoiced);
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
